import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { portalApi } from '../../lib/parceiroApi';
import { useParceiroAuth } from '../../contexts/ParceiroAuthContext';
import { formatCurrency, formatDate } from '../../lib/utils';
import ParceiroStatusBadge from '../../components/ParceiroStatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { PlusCircle, AlertTriangle, Loader2 } from 'lucide-react';

const CardStat = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-[11px] text-gray-500 font-medium mt-0.5">{label}</div>
  </div>
);

export default function ParceiroDashboard() {
  const { user } = useParceiroAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    portalApi.dashboard().then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={28} /></div>;

  const { cards, limite, ultimas, inadimplente } = data;
  const pct = limite.total > 0 ? Math.min(100, (limite.utilizado / limite.total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="parceiro-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-[#3D4F5F]">Olá, {user?.nome?.split(' ')[0]} 👋</h1>
        <Button onClick={() => navigate('/parceiro/nova-solicitacao')} className="bg-[#2B7A3D] hover:bg-[#236632] h-11 px-5 text-base shadow-md" data-testid="btn-solicitar-servico">
          <PlusCircle size={19} className="mr-2" /> Solicitar Serviço
        </Button>
      </div>

      {inadimplente && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-start gap-2 text-sm" data-testid="alerta-inadimplente">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span><b>Pagamento em atraso.</b> Há fatura vencida — novas OS exigem pagamento antecipado até a regularização. Veja em <Link to="/parceiro/faturas" className="underline font-medium">Faturas</Link>.</span>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        <CardStat label="Abertas" value={cards.abertas} color="text-blue-600" />
        <CardStat label="Agendadas" value={cards.agendadas} color="text-indigo-600" />
        <CardStat label="Em andamento" value={cards.em_andamento} color="text-amber-600" />
        <CardStat label="Concluídas" value={cards.concluidas} color="text-green-600" />
        <CardStat label="Canceladas" value={cards.canceladas} color="text-red-500" />
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-[#3D4F5F]">Limite de crédito</h2>
            <Link to="/parceiro/perfil" className="text-xs text-[#2B7A3D] font-medium">Solicitar aumento</Link>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-[#2B7A3D]'}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center" data-testid="limite-resumo">
            <div><div className="text-xs text-gray-500">Limite total</div><div className="font-bold text-sm">{formatCurrency(limite.total)}</div></div>
            <div><div className="text-xs text-gray-500">Utilizado</div><div className="font-bold text-sm text-amber-600">{formatCurrency(limite.utilizado)}</div></div>
            <div><div className="text-xs text-gray-500">Disponível</div><div className="font-bold text-sm text-[#2B7A3D]">{formatCurrency(limite.disponivel)}</div></div>
          </div>
        </CardContent>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-[#3D4F5F]">Últimas solicitações</h2>
          <Link to="/parceiro/solicitacoes" className="text-xs text-[#2B7A3D] font-medium">Ver todas</Link>
        </div>
        {ultimas.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            Nenhuma solicitação ainda. Toque em <b>Solicitar Serviço</b> para começar.
          </div>
        ) : (
          <div className="space-y-2">
            {ultimas.map((o) => (
              <Link key={o.id} to={`/parceiro/solicitacoes/${o.id}`} className="block bg-white rounded-xl border border-gray-200 p-3 hover:border-[#2B7A3D] transition-colors" data-testid={`ultima-os-${o.numero}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#3D4F5F] truncate">#OS-{String(o.numero).padStart(4, '0')} · {o.servico_nome}</div>
                    <div className="text-xs text-gray-500 truncate">{o.cliente_nome} · {o.cidade_atendimento} · {formatDate(o.data_agendamento)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <ParceiroStatusBadge status={o.status} />
                    {o.valor > 0 && <div className="text-xs font-semibold text-gray-700 mt-1">{formatCurrency(o.valor)}</div>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
